import React, { useState, useEffect } from 'react';
import { getProduct, updateProduct } from '../../services/api';
import { Product, ProductFormData } from '../../types';
import ProductForm from './ProductForm';
import { X } from 'lucide-react';
import ContentLoader from '../ui/ContentLoader';

interface ProductEditModalProps {
  productId: string;
  isOpen?: boolean;
  onClose: () => void;
  onSaved: () => Promise<void>;
}

const ProductEditModal: React.FC<ProductEditModalProps> = ({
  productId,
  isOpen = true,
  onClose,
  onSaved
}) => {
  const [product, setProduct] = useState<Product | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Add backdrop effect to body when modal opens
  useEffect(() => {
    if (isOpen) {
      // Add backdrop blur class to body
      document.body.classList.add('modal-open');
      // Disable scrolling on the body
      document.body.style.overflow = 'hidden';
    } else {
      // Remove backdrop blur class from body
      document.body.classList.remove('modal-open');
      // Re-enable scrolling
      document.body.style.overflow = '';
    }
    
    // Cleanup on unmount
    return () => {
      document.body.classList.remove('modal-open');
      document.body.style.overflow = '';
    };
  }, [isOpen]);

  useEffect(() => {
    if (!isOpen) return;
    
    const loadProduct = async () => {
      if (!productId) {
        setError('Invalid product ID');
        setIsLoading(false);
        return;
      }

      try {
        setIsLoading(true);
        const data = await getProduct(productId);
        // Product loaded for editing
        setProduct(data);
      } catch (err) {
        console.error('Error loading product:', err);
        setError('Failed to load product. Please try again.');
      } finally {
        setIsLoading(false);
      }
    };

    loadProduct();
  }, [productId, isOpen]);

  const handleSubmit = async (data: ProductFormData) => {
    if (!productId) return;
    
    setIsSaving(true);
    setError(null);

    try {
      const result = await updateProduct(productId, data);
              // Product updated successfully
      if (onSaved) await onSaved();
      onClose();
    } catch (err: any) {
      console.error('Error updating product:', err);
      setError(err.response?.data?.error || 'Failed to update product. Please try again.');
    } finally {
      setIsSaving(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black/60 backdrop-blur-sm z-[9999] flex items-center justify-center p-4 overflow-hidden">
      <div className="bg-background-card rounded-lg shadow-xl w-full max-w-5xl max-h-[90vh] overflow-y-auto animate-modalOpen scroll-smooth">
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b border-border-dark sticky top-0 bg-background-card z-10">
          <h2 className="text-xl font-semibold">
            {product ? `Edit ${product.name}` : 'Edit Product'}
          </h2>
          <button 
            onClick={onClose}
            className="p-2 rounded-full hover:bg-background-hover text-gray-400 hover:text-gray-200 transition-colors"
            aria-label="Close"
          >
            <X size={24} />
          </button>
        </div>

        {/* Content */}
        <div className="p-6">
          {isLoading ? (
            <ContentLoader
              message="Loading product..."
              size="lg"
              withCard={false}
              minHeight="300px"
            />
          ) : error && !product ? (
            <div className="flex flex-col items-center justify-center py-8">
              <h2 className="text-xl font-semibold mb-2">{error}</h2>
              <button 
                onClick={onClose} 
                className="premium-button-primary mt-4"
              >
                Close
              </button>
            </div>
          ) : (
            <>
              {error && (
                <div className="bg-status-danger/10 border border-status-danger/20 text-status-danger rounded-md p-4 mb-6">
                  <p>{error}</p>
                </div>
              )}

              {product && (
                <ProductForm 
                  initialProduct={product} 
                  onSubmit={handleSubmit} 
                  isLoading={isSaving} 
                />
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default ProductEditModal; 